import { Link } from 'react-router-dom';

const services = [
  {
    id: 'fences',
    tag: 'Privacy · Security · Curb Appeal',
    title: 'Expert Fence Installation',
    description: 'Vinyl, wood, chain link, and aluminum fences crafted to elevate your property across Ormond Beach, Daytona Beach, and Volusia County — built to outlast Florida weather.',
    image: 'https://public.readdy.ai/ai/img_res/8ba9ae9e9744f07feb7e966a039ae636.jpg',
    icon: 'ri-home-4-line',
  },
  {
    id: 'pavers',
    tag: 'Driveways · Patios · Pool Decks',
    title: 'Custom Paver Systems',
    description: 'Precision-installed paver systems that transform your driveway, patio, or pool deck into a lasting statement. From Palm Coast to Port Orange — built to impress.',
    image: 'https://readdy.ai/api/search-image?query=luxury%20natural%20stone%20paver%20patio%20outdoor%20living%20space%20Florida%20high-end%20residential%20elegant%20outdoor%20furniture%20pool%20deck%20warm%20afternoon%20golden%20light%20premium%20craftsmanship%20sophisticated%20design%20upscale&width=800&height=600&seq=dippels-svc-pavers&orientation=landscape',
    icon: 'ri-layout-grid-line',
  },
  {
    id: 'landscape',
    tag: 'Sod · Planting · Grading · Mulch',
    title: 'Landscape Installation',
    description: 'Degree-backed landscape design and installation — from lush sod to tropical planting beds — tailored to Florida\'s unique climate and soil conditions.',
    image: 'https://readdy.ai/api/search-image?query=luxury%20residential%20landscape%20installation%20Florida%20lush%20tropical%20plants%20manicured%20emerald%20sod%20lawn%20premium%20planting%20beds%20neat%20mulch%20professional%20landscaping%20vibrant%20green%20upscale%20home%20beautiful%20curb%20appeal&width=800&height=600&seq=dippels-svc-landscape&orientation=landscape',
    icon: 'ri-plant-line',
  },
  {
    id: 'lighting',
    tag: 'Path · Accent · Irrigation · Drip',
    title: 'Landscape Lighting & Irrigation',
    description: 'Dramatic outdoor lighting that transforms your property after dark, paired with smart irrigation systems designed for Florida conservation and efficiency.',
    image: 'https://readdy.ai/api/search-image?query=elegant%20outdoor%20landscape%20lighting%20luxury%20Florida%20home%20dusk%20pathway%20lights%20uplighting%20palm%20trees%20warm%20amber%20golden%20glow%20dramatic%20nighttime%20garden%20scene%20premium%20outdoor%20lighting%20design%20sophisticated&width=800&height=600&seq=dippels-svc-lighting&orientation=landscape',
    icon: 'ri-sun-line',
  },
  {
    id: 'sheds',
    tag: 'Storage · Workshop · Garden · Metal',
    title: 'Custom Sheds',
    description: 'Florida-code-compliant custom sheds built with premium materials to complement your property\'s aesthetic — from Edgewater to New Smyrna Beach.',
    image: 'https://readdy.ai/api/search-image?query=premium%20custom%20shed%20luxury%20backyard%20Florida%20property%20high-end%20storage%20building%20elegant%20design%20manicured%20lawn%20professional%20quality%20construction%20beautiful%20landscaping%20upscale%20residential%20clean%20lines&width=800&height=600&seq=dippels-svc-sheds&orientation=landscape',
    icon: 'ri-store-2-line',
  },
];

export default function ServicesOverview() {
  return (
    <section className="bg-cream py-28 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16 items-end">
          <div>
            <p className="text-gold font-sans text-[11px] font-semibold tracking-[0.28em] uppercase mb-4 flex items-center gap-3">
              <span className="h-px w-8 bg-gold inline-block"></span>
              Our Expertise
            </p>
            <h2 className="font-serif text-charcoal font-bold leading-tight" style={{ fontSize: 'clamp(34px, 3.5vw, 52px)' }}>
              Five Disciplines.<br />One Elite Team.
            </h2>
          </div>
          <div>
            <p className="text-charcoal-muted text-base leading-relaxed">
              From premium fencing and pavers to full landscape installations, architectural lighting, and custom sheds — Dippel's delivers elite results across Florida's east coast with degree-backed craftsmanship and uncompromising attention to detail.
            </p>
          </div>
        </div>

        {/* Primary 3-card row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
          {services.slice(0, 3).map((svc) => (
            <ServiceCard key={svc.id} svc={svc} tall />
          ))}
        </div>

        {/* Secondary 2-card row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {services.slice(3).map((svc) => (
            <ServiceCard key={svc.id} svc={svc} tall={false} />
          ))}
        </div>

        {/* CTA after services */}
        <div className="mt-14 flex flex-col sm:flex-row items-center justify-between gap-6 bg-charcoal rounded-2xl px-8 py-7">
          <div>
            <p className="font-serif text-white text-xl font-semibold mb-1">
              Not sure which service you need?
            </p>
            <p className="text-white/50 text-sm">We&apos;ll walk you through everything — no pressure, no obligation.</p>
          </div>
          <div className="flex gap-3 flex-shrink-0">
            <a
              href="tel:3863014399"
              className="inline-flex items-center gap-2 border border-white/20 text-white text-sm font-semibold px-6 py-3 rounded-full hover:border-white/50 transition-colors whitespace-nowrap"
            >
              <i className="ri-phone-fill text-gold"></i>
              Call Us
            </a>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 bg-gold text-charcoal text-sm font-bold px-6 py-3 rounded-full hover:bg-gold-light transition-colors whitespace-nowrap"
            >
              View All Services
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ svc, tall }: { svc: typeof services[0]; tall: boolean }) {
  return (
    <div className={`group relative overflow-hidden rounded-2xl cursor-pointer ${tall ? 'h-[380px]' : 'h-[300px]'}`}>
      {/* Image */}
      <img
        src={svc.image}
        alt={`${svc.title} — Dippel's Outdoor Services Florida`}
        className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-700 ease-out"
      />

      {/* Base overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent"></div>

      {/* Hover overlay */}
      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

      {/* Top tag */}
      <div className="absolute top-5 left-5">
        <div className="flex items-center gap-1.5 bg-black/40 backdrop-blur-sm border border-white/10 rounded-full px-3 py-1.5">
          <i className={`${svc.icon} text-gold text-xs`}></i>
          <span className="text-white/80 text-[10px] font-medium tracking-wide">{svc.tag}</span>
        </div>
      </div>

      {/* Bottom content */}
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <h3 className="font-serif text-[22px] text-white font-bold mb-2 leading-tight">{svc.title}</h3>
        <p className="text-white/60 text-[13px] leading-relaxed mb-4 max-h-0 overflow-hidden group-hover:max-h-20 transition-all duration-500 ease-out">
          {svc.description}
        </p>
        <Link
          to={`/services#${svc.id}`}
          className="inline-flex items-center gap-1.5 text-gold text-[13px] font-semibold group-hover:gap-3 transition-all duration-300 whitespace-nowrap"
        >
          Explore Service <i className="ri-arrow-right-line"></i>
        </Link>
      </div>

      {/* Bottom border accent on hover */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
    </div>
  );
}
